import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { sendTaskCreationNotification, sendTaskStatusChangeNotification, createNotification, deleteTaskNotifications } from '../../../services/notificationService';
import { getTeamDeliverables } from '../../../services/deliverableService';

const TaskManager = ({ tasks, onTaskUpdate, onTaskCreate, teamMembers, teamId }) => {
  const { user } = useAuth();
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [localTasks, setLocalTasks] = useState([]);
  const [teamDeliverables, setTeamDeliverables] = useState([]);
  const [allTeamMembers, setAllTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assignee: '',
    priority: 'medium',
    dueDate: '',
    category: ''
  });

  // Derived state for team stages (deliverables)
  const teamStages = teamDeliverables || [];

  const priorityOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'high', label: 'High Priority' },
    { value: 'urgent', label: 'Urgent' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'blocked', label: 'Blocked' }
  ];

  // Load team members from database
  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!teamId) return;

      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('id, full_name, email, role, team_role')?.eq('team_id', teamId);

        if (error) throw error;

        setAllTeamMembers(data || []);
      } catch (err) {
        console.error('Error fetching team members:', err);
        setError('Failed to load team members');
      }
    };

    fetchTeamMembers();
  }, [teamId]);

  // Load team deliverables from database
  useEffect(() => {
    const fetchTeamDeliverables = async () => {
      if (!teamId) return;

      try {
        const deliverables = await getTeamDeliverables(teamId);
        setTeamDeliverables(deliverables || []);
      } catch (err) {
        console.error('Error fetching team deliverables:', err);
      }
    };

    fetchTeamDeliverables();
  }, [teamId]);

  // Load tasks from database
  useEffect(() => {
    const fetchTasks = async () => {
      if (!teamId) return;

      try {
        setLoading(true);
        const { data, error } = await supabase?.from('team_tasks')?.select(`
            *,
            assignee:user_profiles!team_tasks_assignee_id_fkey(id, full_name)
          `)?.eq('team_id', teamId)?.order('created_at', { ascending: false });

        if (error) throw error;

        setLocalTasks(data || []);
      } catch (err) {
        console.error('Error fetching tasks:', err);
        setError('Failed to load tasks');
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [teamId]);

  // Subscribe to real-time task updates
  useEffect(() => {
    if (!teamId) return;

    const tasksChannel = supabase?.channel('team_tasks_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'team_tasks',
          filter: `team_id=eq.${teamId}`
        },
        (payload) => {
          console.log('Task change detected:', payload);
          // Reload tasks when changes occur
          fetchTasks();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(tasksChannel);
    };
  }, [teamId]);

  const memberOptions = allTeamMembers?.map(member => ({
    value: member?.id,
    label: `${member?.full_name} ${member?.team_role ? `(${member?.team_role})` : ''}`
  }));

  const categoryOptions = teamDeliverables?.map(deliverable => ({
    value: deliverable?.deliverableName,
    label: deliverable?.deliverableName
  }));

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-error bg-error/10 border-error/20';
      case 'high': return 'text-warning bg-warning/10 border-warning/20';
      case 'medium': return 'text-primary bg-primary/10 border-primary/20';
      case 'low': return 'text-muted-foreground bg-muted border-border';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success/10';
      case 'in-progress': return 'text-warning bg-warning/10';
      case 'pending': return 'text-muted-foreground bg-muted';
      case 'blocked': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'in-progress': return 'Clock';
      case 'pending': return 'Circle';
      case 'blocked': return 'AlertCircle';
      default: return 'Circle';
    }
  };

  const handleCreateTask = async () => {
    if (!newTask?.title?.trim() || !teamId) {
      setError('Task title is required');
      return;
    }

    try {
      const taskData = {
        team_id: teamId,
        title: newTask?.title?.trim(),
        description: newTask?.description?.trim() || null,
        assignee_id: newTask?.assignee || null,
        priority: newTask?.priority,
        due_date: newTask?.dueDate || null,
        category: newTask?.category || null,
        status: 'pending',
        created_by: user?.id
      };

      const { data, error: insertError } = await supabase?.from('team_tasks')?.insert([taskData])?.select(`
          *,
          assignee:user_profiles!team_tasks_assignee_id_fkey(id, full_name),
          creator:user_profiles!team_tasks_created_by_fkey(id, full_name)
        `)?.single();

      if (insertError) throw insertError;

      // Create notification in database
      try {
        const assigneeName = data?.assignee?.full_name || 'Unassigned';
        const creatorName = data?.creator?.full_name || 'Unknown';
        const categoryName = newTask?.category || 'Uncategorized';

        await createNotification({
          team_id: teamId,
          user_id: data?.assignee_id || null,
          type: 'task',
          title: 'New Task Assigned',
          message: `${creatorName} assigned you a task: "${data?.title}" in category "${categoryName}"`,
          reference_id: data?.id,
          reference_type: 'task'
        });
      } catch (notifError) {
        console.warn('⚠️ Failed to create notification record:', notifError);
      }

      // Send email/SMS notifications
      try {
        await sendTaskCreationNotification({
          ...data,
          team_id: teamId,
          creator_id: user?.id,
          assignee_id: data?.assignee_id
        });
      } catch (notifError) {
        console.warn('⚠️ Task created but email notification failed:', notifError);
      }

      setLocalTasks(prev => [data, ...prev]);
      
      setNewTask({
        title: '',
        description: '',
        assignee: '',
        priority: 'medium',
        dueDate: '',
        category: ''
      });
      setShowCreateTask(false);
      setError('');

      if (onTaskCreate) {
        onTaskCreate(data);
      }
    } catch (err) {
      console.error('Error creating task:', err);
      setError('Failed to create task: ' + err?.message);
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus, oldStatus) => {
    try {
      const { error } = await supabase?.from('team_tasks')?.update({ 
          status: newStatus,
          updated_at: new Date()?.toISOString()
        })?.eq('id', taskId);

      if (error) throw error;

      // Get task details for notification
      const task = localTasks?.find(t => t?.id === taskId);

      // Create notification in database
      try {
        const { data: updater } = await supabase?.from('user_profiles')?.select('full_name')?.eq('id', user?.id)?.single();
        
        await createNotification({
          team_id: teamId,
          user_id: task?.created_by,
          type: 'task',
          title: 'Task Status Changed',
          message: `${updater?.full_name || 'Someone'} changed "${task?.title}" status from ${oldStatus} to ${newStatus}`,
          reference_id: taskId,
          reference_type: 'task'
        });
      } catch (notifError) {
        console.warn('⚠️ Failed to create notification record:', notifError);
      }

      // Send email/SMS notifications
      try {
        await sendTaskStatusChangeNotification({
          title: task?.title,
          previous_status: oldStatus,
          new_status: newStatus,
          changed_by_id: user?.id,
          team_id: teamId
        });
      } catch (notifError) {
        console.warn('⚠️ Status updated but notification failed:', notifError);
      }

      setLocalTasks(prev => 
        prev?.map(task => 
          task?.id === taskId 
            ? { ...task, status: newStatus }
            : task
        )
      );

      if (onTaskUpdate) {
        onTaskUpdate(taskId, { status: newStatus });
      }
    } catch (err) {
      console.error('Error updating task status:', err);
      setError('Failed to update task status: ' + err?.message);
    }
  };

  const handleDeleteTask = async (taskId, creatorId) => {
    if (creatorId !== user?.id) {
      setError('Only the task creator can delete this task');
      return;
    }

    if (!confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      return;
    }

    try {
      // Get task info before deletion
      const task = localTasks?.find(t => t?.id === taskId);
      
      // Delete the task
      const { error: deleteError } = await supabase?.from('team_tasks')?.delete()?.eq('id', taskId);

      if (deleteError) throw deleteError;

      // Delete related notifications
      try {
        await deleteTaskNotifications(taskId);
      } catch (notifError) {
        console.warn('⚠️ Task deleted but notification cleanup failed:', notifError);
      }

      // Create deletion notification to inform assignee
      try {
        const { data: creator } = await supabase?.from('user_profiles')?.select('full_name')?.eq('id', user?.id)?.single();

        if (task?.assignee_id) {
          await createNotification({
            team_id: teamId,
            user_id: task?.assignee_id,
            type: 'task',
            title: 'Task Deleted',
            message: `${creator?.full_name || 'Task creator'} deleted the task: "${task?.title}"`,
            reference_id: null,
            reference_type: 'task'
          });
        }
      } catch (notifError) {
        console.warn('⚠️ Failed to create deletion notification:', notifError);
      }

      // Remove from local state
      setLocalTasks(prev => prev?.filter(task => task?.id !== taskId));
      setError('');
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task: ' + err?.message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString();
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date() && dueDate;
  };

  const getMemberName = (task) => {
    return task?.assignee?.full_name || 'Unassigned';
  };

  const getMemberAvatar = (task) => {
    if (!task?.assignee?.full_name) return '?';
    return task?.assignee?.full_name?.split(' ')?.map(n => n?.[0])?.join('');
  };

  const getCategoryName = (task) => {
    return task?.category || 'Uncategorized';
  };

  const tasksByStatus = {
    pending: localTasks?.filter(task => task?.status === 'pending'),
    in_progress: localTasks?.filter(task => task?.status === 'in_progress'),
    completed: localTasks?.filter(task => task?.status === 'completed'),
    blocked: localTasks?.filter(task => task?.status === 'blocked')
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader2" size={32} className="animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading tasks...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Error Banner */}
      {error && (
        <div className="mb-4 bg-destructive/10 border border-destructive/20 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-destructive">{error}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setError('')}
              className="text-destructive hover:bg-destructive/20"
            >
              <Icon name="X" size={14} />
            </Button>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Task Manager</h2>
        <Button 
          variant="default" 
          size="sm" 
          iconName="Plus" 
          iconPosition="left"
          onClick={() => setShowCreateTask(true)}
        >
          New Task
        </Button>
      </div>
      {/* Create Task Form */}
      {showCreateTask && (
        <div className="mb-6 p-4 border border-border rounded-lg bg-muted/50">
          <h3 className="text-lg font-medium text-foreground mb-4">Create New Task</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Task Title"
              type="text"
              placeholder="Enter task title"
              value={newTask?.title}
              onChange={(e) => setNewTask(prev => ({ ...prev, title: e?.target?.value }))}
              required
            />
            <Select
              label="Assignee"
              placeholder="Select team member..."
              options={[
                { value: '', label: 'Unassigned' },
                ...memberOptions
              ]}
              value={newTask?.assignee}
              onChange={(value) => setNewTask(prev => ({ ...prev, assignee: value }))}
            />
            <Select
              label="Priority"
              options={priorityOptions}
              value={newTask?.priority}
              onChange={(value) => setNewTask(prev => ({ ...prev, priority: value }))}
            />
            <Select
              label="Category (Stage)"
              placeholder="Select stage..."
              options={[
                { value: '', label: teamStages?.length === 0 ? 'No stages available for this team' : 'Select stage' },
                ...categoryOptions
              ]}
              value={newTask?.category}
              onChange={(value) => setNewTask(prev => ({ ...prev, category: value }))}
              disabled={teamStages?.length === 0}
            />
            <Input
              label="Due Date"
              type="date"
              value={newTask?.dueDate}
              onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e?.target?.value }))}
            />
          </div>
          <div className="mt-4">
            <Input
              label="Description"
              type="text"
              placeholder="Enter task description"
              value={newTask?.description}
              onChange={(e) => setNewTask(prev => ({ ...prev, description: e?.target?.value }))}
            />
          </div>
          <div className="flex items-center space-x-2 mt-4">
            <Button variant="default" onClick={handleCreateTask}>
              Create Task
            </Button>
            <Button variant="outline" onClick={() => {
              setShowCreateTask(false);
              setError('');
            }}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {/* Task Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {Object.entries(tasksByStatus)?.map(([status, statusTasks]) => (
          <div key={status} className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-md font-medium text-foreground capitalize">
                {status?.replace('_', ' ')}
              </h3>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                {statusTasks?.length}
              </span>
            </div>
            
            <div className="space-y-3">
              {statusTasks?.map((task) => (
                <div key={task?.id} className="p-4 border border-border rounded-lg bg-card hover:shadow-soft transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="text-sm font-medium text-foreground line-clamp-2 flex-1">
                      {task?.title}
                    </h4>
                    
                    <div className="flex items-center space-x-1 ml-2">
                      {/* Status Dropdown for Assignee */}
                      {task?.assignee_id === user?.id && (
                        <div className="ml-2">
                          <Select
                            value={task?.status}
                            onChange={(value) => handleUpdateTaskStatus(task?.id, value, task?.status)}
                            options={statusOptions}
                            className="text-xs"
                          />
                        </div>
                      )}
                      
                      {/* Delete Button for Creator */}
                      {task?.created_by === user?.id && (
                        <button
                          onClick={() => handleDeleteTask(task?.id, task?.created_by)}
                          className="p-1.5 hover:bg-destructive/10 rounded transition-colors"
                          title="Delete task"
                        >
                          <Icon name="Trash2" size={14} className="text-destructive" />
                        </button>
                      )}
                    </div>
                  </div>

                  {task?.description && (
                    <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                      {task?.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between mb-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(task?.priority)}`}>
                      {task?.priority}
                    </span>
                    <span className="text-xs text-muted-foreground capitalize">
                      {getCategoryName(task)}
                    </span>
                  </div>

                  {task?.assignee_id && (
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-primary-foreground">
                          {getMemberAvatar(task)}
                        </span>
                      </div>
                      <span className="text-xs text-foreground">
                        {getMemberName(task)}
                      </span>
                    </div>
                  )}

                  {task?.due_date && (
                    <div className="flex items-center space-x-1">
                      <Icon 
                        name="Calendar" 
                        size={12} 
                        className={isOverdue(task?.due_date) ? 'text-error' : 'text-muted-foreground'} 
                      />
                      <span className={`text-xs ${isOverdue(task?.due_date) ? 'text-error' : 'text-muted-foreground'}`}>
                        {formatDate(task?.due_date)}
                        {isOverdue(task?.due_date) && ' (Overdue)'}
                      </span>
                    </div>
                  )}
                </div>
              ))}
              
              {statusTasks?.length === 0 && (
                <div className="p-4 border border-dashed border-border rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">
                    No {status?.replace('_', ' ')} tasks
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TaskManager;